import { Price } from '@shared/models/price.model';
import { PaymentType, UpfrontPaymentMethod } from '@shared/enums/payment-type';
import { Sponsor } from '@shared/models/sponsor.model';
import {
  CreditCheckedPerson,
  EquipmentUpgradeAllowance,
  ProductAllowance,
  TradeInAllowance,
} from '@core/models/initialize-response.model';
import { PhoneBookVisibility } from '@app/shared/models/subscriber.model';
import { BindingType } from '@app/sales/models/binding-type.enums';

export enum CaptureType {
  CreateSubscriptionWithGeneratedANumber = 'CreateSubscriptionWithGeneratedANumber',
  CreateSubscriptionWithCustomerSelectedANumber = 'CreateSubscriptionWithCustomerSelectedANumber',
  CreateSubscriptionWithPorting = 'CreateSubscriptionWithPorting',
  CreateMBBSubscription = 'CreateMBBSubscription',
  CreateMBBSubscriptionWithHardware = 'CreateMBBSubscriptionWithHardware',
  CreateMBBSubscriptionWithHardwareAndLogistics = 'CreateMBBSubscriptionWithHardwareAndLogistics',
  ChangeSubscriptionOnExistingCustomer = 'ChangeSubscriptionOnExistingCustomer',
  ChangeSubscriptionOnExistingCustomerWithSubscriber = 'ChangeSubscriptionOnExistingCustomerWithSubscriber',
  ChangeSubscriberOnExistingSubscription = 'ChangeSubscriberOnExistingSubscription',
  AddEquipment = 'AddEquipment',
  ChangeMbbSubscriptionOnExistingCustomer = 'ChangeMbbSubscriptionOnExistingCustomer',
  UpdateServicesOnExistingSubscription = 'UpdateServicesOnExistingSubscription',
  CreateCellPhoneBundleWithGeneratedANumber = 'CreateCellPhoneBundleWithGeneratedANumber',
  CreateCellPhoneBundleWithCustomerSelectedANumber = 'CreateCellPhoneBundleWithCustomerSelectedANumber',
  CreateCellPhoneBundleWithPorting = 'CreateCellPhoneBundleWithPorting',
  CreateCellPhoneBundleWithExistingSubscription = 'CreateCellPhoneBundleWithExistingSubscription',
  CreateAccessoryBundleWithCustomerSelectedANumber = 'CreateAccessoryBundleWithCustomerSelectedAnumber',
  CreateAccessoryBundleWithPorting = 'CreateAccessoryBundleWithPorting',
  CreateAccessoryBundleWithExistingSubscription = 'CreateAccessoryBundleWithExistingSubscription',
  NotImplemented = 'NotImplemented',
  ChangeVoiceSimCard = 'ChangeVoiceSimCard', // deprecated, we keep this in order to not break existing orders
  ChangeSimCard = 'ChangeSimCard',
  SendReturnBox = 'SendReturnBox',
  CreateCustomerService = 'CreateCustomerService',
  AddMobileInsurance = 'AddMobileInsurance',
  AddTradeInVoucher = 'AddTradeInVoucher',
  AddVendorSubsidyVoucher = 'AddVendorSubsidyVoucher',
}

export interface SubscriptionWithCustomerSelectedANumber {
  subscriber: CaptureSubscriber;
  additionalServices: AdditionalService[];
  aNumber: string;
  reservationId: number;
  icc: string;
  orderSim: boolean;
  isESim: boolean;
  eSimActivationEmailAddress?: string;
  idVerification: IdVerification;
  startDate: Date;
  sponsor: Sponsor;
  whitelistEntries: string[];
}

export interface CreateSubscriptionWithCustomerSelectedAnumber {
  paymentType: PaymentType;
  subscription: SubscriptionWithCustomerSelectedANumber;
  idVerification: IdVerification;
}

export interface SubscriptionWithGeneratedANumber {
  subscriber: CaptureSubscriber;
  additionalServices: AdditionalService[];
  icc: string;
  orderSim: boolean;
  isESim: boolean;
  eSimActivationEmailAddress?: string;
  idVerification: IdVerification;
  startDate: Date;
  sponsor: Sponsor;
  whitelistEntries: string[];
}

export interface CreateSubscriptionWithGeneratedANumber {
  paymentType: PaymentType;
  subscription: SubscriptionWithGeneratedANumber;
  idVerification: IdVerification;
}

export interface SubscriptionWithPorting {
  subscriber: CaptureSubscriber;
  additionalServices: AdditionalService[];
  aNumber: string;
  keepExistingSim: boolean;
  existingOwnerCustomerId: string;
  portingDateTime: Date;
  icc: string;
  orderSim: boolean;
  isESim: boolean;
  eSimActivationEmailAddress?: string;
  idVerification: IdVerification;
  sponsor: Sponsor;
  whitelistEntries: string[];
}

export interface CreateSubscriptionWithPorting {
  paymentType: PaymentType;
  subscription: SubscriptionWithPorting;
  idVerification: IdVerification;
}

export interface CreateMBBSubscription {
  subscriber: CaptureSubscriber;
  subscriptionInfo: SubscriptionInfo;
  icc: string;
  description: string;
  routerName: string;
  additionalServices: AdditionalService[];
  paymentType: PaymentType;
  idVerification: IdVerification;
  startDate?: Date;
}

export interface CreateMBBSubscriptionWithHardware {
  paymentType: PaymentType;
  subscriber: CaptureSubscriber;
  equipment: Equipment;
  subscriptionInfo: SubscriptionInfo;
  icc: string;
  description: string;
  routerName: string;
  isSoldByExternalRetailOnly: boolean;
  additionalServices: AdditionalService[];
  idVerification: IdVerification;
  startDate?: Date;
}

export interface CreateMBBSubscriptionWithHardwareAndLogistics {
  paymentType: PaymentType;
  subscriber: CaptureSubscriber;
  equipment: Equipment;
  subscriptionInfo: SubscriptionInfo;
  description: string;
  routerName: string;
  subsidyId: string;
  additionalServices: AdditionalService[];
  hasShipment: boolean;
  idVerification: IdVerification;
  startDate?: Date;
}

export interface SubscriptionInfo {
  subscriptionOfferingId: string;
  subscriptionName: string;
  monthlyPrice: Price;
  originalMonthlyPrice: Price;
  bindingPeriodMonths: number;
  terminationFee: Price;
  campaignPeriodMonths: number;
}

export interface ChangeSubscriptionOnExistingCustomer extends ChangeSubscriptionOnExistingCustomerWithSubscriber {}

export interface ChangeSubscriptionOnExistingCustomerWithSubscriber {
  paymentType: PaymentType;
  subscriptionId: string;
  aNumber: string;
  additionalServices: AdditionalService[];
  imei: string;
  idVerification: IdVerification;
  previousProductName: string;
  subscriber: CaptureSubscriber;
  bindingType?: BindingType;
}

export interface ChangeSubscriberOnExistingSubscription {
  paymentType: PaymentType;
  subscriptionId: string;
  subscriber: CaptureSubscriber;
  idVerification: IdVerification;
}

export interface ChangeMbbSubscriptionOnExistingCustomer {
  paymentType: PaymentType;
  subscriptionId: string;
  subscriptionInfo: SubscriptionInfo;
  idVerification: IdVerification;
}

export interface UpdateServicesOnExistingSubscription {
  paymentType: PaymentType;
  subscriptionId: string;
  additionalServices: AdditionalService[];
  idVerification: IdVerification;
}

export interface CaptureChangeSimCard {
  paymentType: PaymentType;
  hasShipment: boolean;
  subscriptionId: string;
  icc: string;
  isESim: boolean;
  aNumber: string;
  startDate: Date;
  idVerification: IdVerification;
}

export interface CreateCellPhoneBundleBase {
  subsidyId: string;
  subscriptionOfferingId: string;
  paymentType: PaymentType;
  hasShipment: boolean;
  isExternalCellPhone: boolean;
  cellPhoneAgreement: CellPhoneAgreement;
  idVerification: IdVerification;
  insuranceOptionOfferingId: string;
  insuranceOptionOfferingName: string;
}

export interface CreateCellPhoneBundleWithCustomerSelectedANumber extends CreateCellPhoneBundleBase {
  subscription: SubscriptionWithCustomerSelectedANumber;
  eid?: string;
}

export interface CreateCellPhoneBundleWithGeneratedANumber extends CreateCellPhoneBundleBase {
  subscription: SubscriptionWithGeneratedANumber;
}

export interface CreateCellPhoneBundleWithPorting extends CreateCellPhoneBundleBase {
  subscription: SubscriptionWithPorting;
}

export interface CreateCellPhoneBundleWithExistingSubscription extends CreateCellPhoneBundleBase {
  existingSubscriptionId: number;
  msisdn: string;
}

export interface CaptureTradeInProduct {
  voucherId: string;
  amount: Price;
  tradeInId: number;
  imei: string;
  deviceVendor: string;
  deviceModel: string;
  storageCapacity: string;
  idVerification: IdVerification;
  paymentType: PaymentType | UpfrontPaymentMethod.NoUpfrontPayment;
  hasShipment: boolean;
}

export interface CaptureVendorSubsidyVoucher {
  voucherId: string;
  amount: Price;
  vendorName: string;
  periodName: string;
  validUntil: Date;
  purchasedDevices: string[];
  idVerification: IdVerification;
  paymentType: PaymentType | UpfrontPaymentMethod.NoUpfrontPayment;
  hasShipment: boolean;
}

export interface CreateAccessoryBundleWithCustomerSelectedANumber {
  subscription: SubscriptionWithCustomerSelectedANumber;
  subsidyId: string;
  subscriptionOfferingId: string;
  paymentType: PaymentType;
  name: string;
  hasShipment: true;
  imageUrl: string;
  totalPrice: Price;
  idVerification: IdVerification;
  gtin: string;
  imei: string;
  isManualRegistration: boolean;
}

export interface CreateAccessoryBundleWithPorting {
  subscription: SubscriptionWithPorting;
  subsidyId: string;
  subscriptionOfferingId: string;
  paymentType: PaymentType;
  name: string;
  hasShipment: true;
  imageUrl: string;
  totalPrice: Price;
  idVerification: IdVerification;
  gtin: string;
  imei: string;
  isManualRegistration: boolean;
}

export interface CreateAccessoryBundleWithExistingSubscription {
  existingSubscriptionId: string;
  subsidyId: string;
  subscriptionOfferingId: string;
  hasShipment: boolean;
  paymentType: PaymentType;
  name: string;
  imageUrl: string;
  totalPrice: Price;
  idVerification: IdVerification;
  gtin: string;
  imei: string;
  isManualRegistration: boolean;
}

export interface AddEquipment {
  subscriptionId: string;
  hasShipment: boolean;
  equipment: Equipment;
  insuranceOptionOfferingId: string;
  idVerification: IdVerification;
}

export interface AddMobileInsurance {
  imei: string;
  idVerification: IdVerification;
  hasShipment: boolean;
}

export interface CaptureSendReturnBox {
  paymentType: PaymentType;
  hasShipment: boolean;
  idVerification: IdVerification;
}

export interface CaptureCreateCustomerService {
  paymentType: PaymentType;
  hasShipment: boolean;
  idVerification: IdVerification;
  name: string;
  monthlyPrice: Price;
}

export interface CellPhoneAgreement {
  cellPhoneAgreementPrice: CellPhoneAgreementPrice;
  imei: string;
  gtin: string;
  redemptionId?: number;
  name: string;
  imageUrl: string;
  isIdentifiersPendingUpdate: boolean;
}

export interface CellPhoneAgreementPrice {
  monthlyPrice: Price;
  terminalPrice: Price;
  monthlyInsuranceCost: Price;
  totalPrice: Price;
  agreementPeriodInMonths: number;
  upgradeAfterNumberOfMonths: number;
}

export interface CaptureDataCellPhoneAgreementPrice {
  MonthlyPrice: CaptureDataPrice;
  TotalPrice: CaptureDataPrice;
  AgreementPeriodInMonths: number;
}

export interface CaptureSubscriber {
  name: string;
  email: string;
  birthdate: Date;
  streetAddress: string;
  postalCode: string;
  addressCity: string;
  phoneBookVisibility: PhoneBookVisibility;
}

export interface CaptureDataSubscriber {
  Name: string;
  Email: string;
  Birthdate: Date;
  StreetAddress: string;
  PostalCode: string;
  AddressCity: string;
  PhoneBookVisibility: PhoneBookVisibility;
}

export interface IdVerification {
  verificationType: string;
  verificationCode: string;
}

export interface AdditionalService {
  productId: string;
  description: string;
  isServiceAdded: boolean;
  shouldBeImplicitlyRemoved: boolean;
  icc: string;
  monthlyPrice: Price;
  isCampaign: boolean;
  isBarringService?: boolean;
}

export interface CaptureDataAdditionalService {
  ProductId: string;
  Description: string;
  IsServiceAdded: boolean;
  shouldBeImplicitlyRemoved: boolean;
  Icc: string;
  MonthlyPrice: CaptureDataPrice;
  IsCampaign: boolean;
}

export interface CaptureDataPrice {
  IncludingVAT: number;
}

export interface ShoppingCartResponse {
  externalShoppingCartId: string;
  items: ShoppingCartItem[];
}

export interface ShoppingCartItem {
  shoppingCartItemId: number;
  offeringId: string;
  captureData: CaptureData;
}

export interface CaptureData {
  captureDataId?: number;
  shoppingCartItemId?: number;
  captureType: CaptureType;
  data: string;
}

export class Equipment {
  offeringId: string;
  name: string;
  description: string;
  gtin: string;
  imei: string;
  price: Price;
  image?: string;
  imageUrl: string;
  isManualRegistration: boolean;
}

export class CaptureRequest {
  captureType: CaptureType;
  payload: string;
}

export interface CreateAuthorizedShoppingCartRequest {
  customerNo: string;
  ssn: string;
  firstName: string;
  lastName: string;
  authorizeForDownPayments: boolean;
}

export interface AuthorizedShoppingCart {
  externalShoppingCartId: string;
  orderServiceId: string;
  creditCheckedEntity: CreditCheckedPerson;
  currentNoOfSubscriptions?: number;
  totalAllowedNoOfSubscriptions: number;
  currentNoOfFreemiumSubscriptions: number;
  downPaymentAllowance: ProductAllowance;
  subscriptionAllowance: ProductAllowance;
  mbbSubscriptionAllowance: ProductAllowance;
  equipmentUpgrade: EquipmentUpgradeAllowance;
  approved: boolean;
  reasonCode: string;
  reason: string;
  tradeIn: TradeInAllowance;
}
